import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, store } from '..';
import {
  ChiusuraCassaInfo_interface,
  ChiusuraCassaCalcoli_interface,
  IstitutiBancari_interface,
  DettaglioIVA_interface,
  DettaglioPagamento_interface,
} from '../../amministrazione/chiusuracassa/interface/ChiusuraCassa_interfaces';
import {
  getChiusuraCassaInfo,
  getListaIstitutiBancari,
  calcolaChiusuraCassa,
  stampaChiusuraCassa,
} from '../../amministrazione/chiusuracassa/api/ChiusuraCassa_api_call';
import { BluAsyncDialog } from '@bludata/components';
import BluAsyncPasswordDialog from '@focus.cloud/src/UI/BluAsyncPasswordDialog';
import dayjs from 'dayjs';
import { dateFormat } from '@focus.cloud/src/utility_objects/const/costants';

export interface ChiusuraCassaSliceState {
  // Dati
  infoChiusura: ChiusuraCassaInfo_interface | null;
  datiRTnumeroScontrini: number | null;
  datiRTContanti: number;
  datiRTcontaPersone: number;
  datiRTAssegni: number;
  datiRTElettronici: number;
  datiRTAltriIncassi: number;
  datiRTTotaleCorrispettivi: number;
  calcoli: ChiusuraCassaCalcoli_interface | null;
  totaleGrigliaIva: number;
  totaleGrigliaModalita: number;
  istitutiBancari: IstitutiBancari_interface[];
  dataChiusura: string | null;
  chiusuraParziale: boolean;

  // Stati di caricamento ed errori
  loading: boolean;
  error: string | null;

  // Step e navigazione
  activeStep: number;

  // Dialog state
  openSceltaDate: boolean;
  openChiusuraGiornaliera: boolean;
}

const initialState: ChiusuraCassaSliceState = {
  // Dati
  infoChiusura: null,
  datiRTnumeroScontrini: null,
  datiRTContanti: 0,
  datiRTcontaPersone: 0,
  datiRTAssegni: 0,
  datiRTElettronici: 0,
  datiRTAltriIncassi: 0,
  datiRTTotaleCorrispettivi: 0,
  calcoli: null,
  totaleGrigliaIva: 0,
  totaleGrigliaModalita: 0,
  istitutiBancari: [],

  dataChiusura: dayjs().format(dateFormat),
  chiusuraParziale: false,

  // Stati di caricamento ed errori
  loading: false,
  error: null,

  // Step e navigazione
  activeStep: 0,

  // Dialog state
  openSceltaDate: true,
  openChiusuraGiornaliera: false,
};

export const ChiusuraCassaSlice = createSlice({
  name: 'chiusuraCassa',
  initialState,
  reducers: {
    setInfoChiusura(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<ChiusuraCassaInfo_interface | null>,
    ) {
      state.infoChiusura = action.payload;
    },
    setDatiRTNumeroScontrini(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      state.datiRTnumeroScontrini = action.payload;
    },
    setDatiRTContanti(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      state.datiRTContanti = action.payload;
    },
    setDatiRTContapersona(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      state.datiRTcontaPersone = action.payload;
    },
    setDatiRTAssegni(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      state.datiRTAssegni = action.payload;
    },
    setDatiRTElettronici(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      state.datiRTElettronici = action.payload;
    },
    setDatiRTAltriIncassi(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      state.datiRTAltriIncassi = action.payload;
    },
    setDatiRTTotaleCorrispettivi(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      state.datiRTTotaleCorrispettivi = action.payload;
    },
    setCalcoliChiusuraCassa(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<ChiusuraCassaCalcoli_interface | null>,
    ) {
      state.calcoli = action.payload;
    },
    setTotaleGrigliaIva(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      state.totaleGrigliaIva = action.payload;
    },
    setTotaleGrigliaModalita(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      state.totaleGrigliaModalita = action.payload;
    },
    setIstitutiBancariChiusuraCassa(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<IstitutiBancari_interface[]>,
    ) {
      state.istitutiBancari = action.payload;
    },
    setContantiDepositoInBanca(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      if (state.calcoli) {
        state.calcoli.versamentoBancaCont = action.payload;
        state.calcoli.fondo =
          state.calcoli.totaleContanti -
          state.calcoli.versamentoBancaCont -
          (state.calcoli.cassaforteContanti || 0);
      }
    },
    setAssegniDepositoInBanca(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      if (state.calcoli) {
        state.calcoli.versamentoBancaAss = action.payload;
        state.calcoli.fondoAssegni =
          state.calcoli.totaleAssegni -
          state.calcoli.versamentoBancaAss -
          (state.calcoli.cassaforteAssegni || 0);
      }
    },
    setContantiDepositoInCassaforte(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      if (state.calcoli) {
        state.calcoli.cassaforteContanti = action.payload;
        state.calcoli.fondo =
          state.calcoli.totaleContanti -
          (state.calcoli.versamentoBancaCont || 0) -
          state.calcoli.cassaforteContanti;
      }
    },
    setAssegniDepositoInCassaforte(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      if (state.calcoli) {
        state.calcoli.cassaforteAssegni = action.payload;
        state.calcoli.fondoAssegni =
          state.calcoli.totaleAssegni -
          (state.calcoli.versamentoBancaAss || 0) -
          state.calcoli.cassaforteAssegni;
      }
    },
    setContantiFondoCassaApertura(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      if (state.calcoli) {
        state.calcoli.fondo = action.payload;
        state.calcoli.cassaforteContanti =
          state.calcoli.totaleContanti -
          (state.calcoli.versamentoBancaCont || 0) -
          state.calcoli.fondo;
      }
    },
    setAssegniFondoCassaApertura(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      if (state.calcoli) {
        state.calcoli.fondoAssegni = action.payload;
        state.calcoli.cassaforteAssegni =
          state.calcoli.totaleAssegni -
          (state.calcoli.versamentoBancaAss || 0) -
          state.calcoli.fondoAssegni;
      }
    },
    setCodiceBanca(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<string>,
    ) {
      if (state.calcoli) {
        state.calcoli.codiceBanca = action.payload;
      }
    },
    setDataChiusura(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<string | null>,
    ) {
      state.dataChiusura = action.payload;
    },
    setChiusuraParziale(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.chiusuraParziale = action.payload;
    },
    setDataApertura(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<string | null>,
    ) {
      state.calcoli.dataApertura = action.payload;
    },
    setLoadingChiusuraCassa(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.loading = action.payload;
    },
    setErrorChiusuraCassa(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<string | null>,
    ) {
      state.error = action.payload;
    },
    setActiveStepChiusuraCassa(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) {
      state.activeStep = action.payload;
    },
    setOpenSceltaDate(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.openSceltaDate = action.payload;
    },
    setOpenChiusuraGiornaliera(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.openChiusuraGiornaliera = action.payload;
    },
    resetStateChiusuraCassa(state: ChiusuraCassaSliceState) {
      return { ...initialState };
    },
    setDescrizioneSquadratura(
      state: ChiusuraCassaSliceState,
      action: PayloadAction<string>,
    ) {
      if (state.calcoli) {
        state.calcoli.descrizioneSquadratura = action.payload;
      }
    },
    aggiornaDettaglioIVA: (
      state: ChiusuraCassaSliceState,
      action: PayloadAction<{
        iva: DettaglioIVA_interface;
        index: number;
      }>,
    ) => {
      if (
        state.calcoli &&
        state.calcoli.iva &&
        action.payload.index >= 0 &&
        action.payload.index < state.calcoli.iva.length
      ) {
        state.calcoli.iva[action.payload.index] = action.payload.iva;
      }
    },
    aggiungiDettaglioIVA: (
      state: ChiusuraCassaSliceState,
      action: PayloadAction<DettaglioIVA_interface>,
    ) => {
      if (state.calcoli && state.calcoli.iva) {
        state.calcoli.iva.push(action.payload);
      }
    },
    eliminaDettaglioIVA: (
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) => {
      if (
        state.calcoli &&
        state.calcoli.iva &&
        action.payload >= 0 &&
        action.payload < state.calcoli.iva.length
      ) {
        state.calcoli.iva.splice(action.payload, 1);
      }
    },
    aggiornaDettaglioPagamento: (
      state: ChiusuraCassaSliceState,
      action: PayloadAction<{
        pagamento: DettaglioPagamento_interface;
        index: number;
      }>,
    ) => {
      if (
        state.calcoli &&
        state.calcoli.pagamenti &&
        action.payload.index >= 0 &&
        action.payload.index < state.calcoli.pagamenti.length
      ) {
        state.calcoli.pagamenti[action.payload.index] =
          action.payload.pagamento;
      }
    },
    aggiungiDettaglioPagamento: (
      state: ChiusuraCassaSliceState,
      action: PayloadAction<DettaglioPagamento_interface>,
    ) => {
      if (state.calcoli && state.calcoli.pagamenti) {
        state.calcoli.pagamenti.push(action.payload);
      }
    },
    eliminaDettaglioPagamento: (
      state: ChiusuraCassaSliceState,
      action: PayloadAction<number>,
    ) => {
      if (
        state.calcoli &&
        state.calcoli.pagamenti &&
        action.payload >= 0 &&
        action.payload < state.calcoli.pagamenti.length
      ) {
        state.calcoli.pagamenti.splice(action.payload, 1);
      }
    },
  },
});

// Thunk Actions
export const caricaInformazioniChiusura = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingChiusuraCassa(true));
    dispatch(setErrorChiusuraCassa(null));
    try {
      const info = await getChiusuraCassaInfo(
        store.getState().chiusuracassa.dataChiusura,
      );

      // Mostra alertMessage se valorizzato e richiedi password
      if (info.alertMessage) {
        await BluAsyncDialog({
          title: 'Attenzione',
          type: 'warning',
          message: info.alertMessage,
          hideDeclineButton: true,
          confirmButton: 'OK',
        });
        console.log('Alert message mostrato:', info.alertMessage);
        // Richiedi password per confermare l'operazione
        const token = await BluAsyncPasswordDialog();
        if (!token) {
          // Utente ha annullato, torna alla selezione data
          dispatch(setInfoChiusura(null));
          dispatch(setOpenChiusuraGiornaliera(false));
          dispatch(setOpenSceltaDate(true));
          dispatch(setLoadingChiusuraCassa(false));
          return;
        }
      }

      // Se canClose è false, resetta e torna alla selezione data
      if (info.canClose === false) {
        dispatch(setInfoChiusura(null));
        dispatch(setOpenChiusuraGiornaliera(false));
        dispatch(setOpenSceltaDate(true));
        dispatch(setLoadingChiusuraCassa(false));
        return;
      }

      dispatch(setInfoChiusura(info));

      const banche = await getListaIstitutiBancari();
      dispatch(setIstitutiBancariChiusuraCassa(banche));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Errore durante il caricamento dei dati';
      await BluAsyncDialog({
        title: 'Attenzione',
        type: 'error',
        message: errorMessage || 'Errore sconosciuto',
        hideDeclineButton: true,
        confirmButton: 'Chiudi',
      });
      // Resetta e torna alla selezione data (error resettato per permettere nuovo tentativo)
      dispatch(setInfoChiusura(null));
      dispatch(setErrorChiusuraCassa(null));
      dispatch(setOpenChiusuraGiornaliera(false));
      dispatch(setOpenSceltaDate(true));
    } finally {
      dispatch(setLoadingChiusuraCassa(false));
    }
  };
};

export const eseguiCalcoliChiusuraCassa = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingChiusuraCassa(true));
    dispatch(setErrorChiusuraCassa(null));
    const parametri = {
      numeroScontrini: store.getState().chiusuracassa.datiRTnumeroScontrini,
      contaPersone: store.getState().chiusuracassa.datiRTcontaPersone,
      contanti: store.getState().chiusuracassa.datiRTContanti,
      assegni: store.getState().chiusuracassa.datiRTAssegni,
      elettronici: store.getState().chiusuracassa.datiRTElettronici,
      altriIncassi: store.getState().chiusuracassa.datiRTAltriIncassi,
      totaleCorrispettivi:
        store.getState().chiusuracassa.datiRTTotaleCorrispettivi,
    };
    try {
      const response = await calcolaChiusuraCassa(
        parametri,
        store.getState().chiusuracassa.infoChiusura.codiceFiliale,
      );
      dispatch(setCalcoliChiusuraCassa(response));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Errore durante il calcolo della chiusura cassa';
      BluAsyncDialog({
        title: 'Attenzione',
        type: 'error',
        message: errorMessage || 'Errore sconosciuto',
        hideDeclineButton: true,
        confirmButton: 'Chiudi',
      });
      dispatch(setErrorChiusuraCassa(errorMessage));
    } finally {
      dispatch(setLoadingChiusuraCassa(false));
    }
  };
};

export const stampaReport = (params: {
  isChiusura: boolean;
  isControllo: boolean;
  stampaSintetica: boolean;
}) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingChiusuraCassa(true));
    dispatch(setErrorChiusuraCassa(null));
    try {
      const result = await stampaChiusuraCassa(params);
      return result;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Errore durante la stampa';
      dispatch(setErrorChiusuraCassa(errorMessage));
      return false;
    } finally {
      dispatch(setLoadingChiusuraCassa(false));
    }
  };
};

export const nextStepChiusuraCassa = () => {
  return (dispatch: AppDispatch) => {
    const currentStep = store.getState().chiusuracassa.activeStep;

    const passiTotali = 6;
    if (currentStep === 1 && store.getState().chiusuracassa.chiusuraParziale) {
      dispatch(setActiveStepChiusuraCassa(passiTotali - 1));
    } else if (currentStep < passiTotali - 1) {
      dispatch(setActiveStepChiusuraCassa(currentStep + 1));
    }
  };
};

export const prevStepChiusuraCassa = () => {
  return (dispatch: AppDispatch) => {
    const currentStep = store.getState().chiusuracassa.activeStep;
    if (currentStep > 0) {
      dispatch(setActiveStepChiusuraCassa(currentStep - 1));
    }
  };
};

export const resetStep = () => {
  return (dispatch: AppDispatch) => {
    dispatch(setActiveStepChiusuraCassa(0));
  };
};

export const {
  setInfoChiusura,
  setCalcoliChiusuraCassa,
  setTotaleGrigliaIva,
  setTotaleGrigliaModalita,
  setIstitutiBancariChiusuraCassa,
  setDataChiusura,
  setChiusuraParziale,
  setLoadingChiusuraCassa,
  setErrorChiusuraCassa,
  setActiveStepChiusuraCassa,
  resetStateChiusuraCassa,
  setDatiRTNumeroScontrini,
  setDatiRTContanti,
  setDatiRTContapersona,
  setDatiRTAssegni,
  setDatiRTElettronici,
  setDatiRTAltriIncassi,
  setDatiRTTotaleCorrispettivi,
  setDescrizioneSquadratura,
  setContantiDepositoInBanca,
  setAssegniDepositoInBanca,
  setContantiDepositoInCassaforte,
  setAssegniDepositoInCassaforte,
  setAssegniFondoCassaApertura,
  setContantiFondoCassaApertura,
  setDataApertura,
  aggiornaDettaglioIVA,
  aggiungiDettaglioIVA,
  eliminaDettaglioIVA,
  aggiornaDettaglioPagamento,
  aggiungiDettaglioPagamento,
  eliminaDettaglioPagamento,
  setCodiceBanca,
  setOpenSceltaDate,
  setOpenChiusuraGiornaliera,
} = ChiusuraCassaSlice.actions;

export default ChiusuraCassaSlice.reducer;
